<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import { api } from "@/api/client";
import { describeError } from "@/utils/errors";
import type { NxEndpointView, UpdateChannel, UpdatePolicy } from "@/api/types";
import SecretReveal from "@/components/SecretReveal.vue";

const { t } = useI18n();

const items = ref<NxEndpointView[]>([]);
const loading = ref(true);
const listError = ref<string | null>(null);
const search = ref("");

const newLogin = ref("");
const newHostname = ref("");
const newDisplay = ref("");
const formError = ref<string | null>(null);
const submitting = ref(false);

const lastSecret = ref<{ password: string; login: string } | null>(null);

async function reload() {
  loading.value = true;
  listError.value = null;
  try {
    const resp = await api.nxEndpoints.list();
    items.value = resp.nx_endpoints;
  } catch (err) {
    listError.value = errorMessage(err);
  } finally {
    loading.value = false;
  }
}

async function submit() {
  formError.value = null;
  submitting.value = true;
  try {
    const created = await api.nxEndpoints.create({
      login: newLogin.value.trim(),
      hostname: newHostname.value.trim(),
      display_name: newDisplay.value.trim() || undefined,
    });
    lastSecret.value = { password: created.password, login: created.login };
    newLogin.value = "";
    newHostname.value = "";
    newDisplay.value = "";
    await reload();
  } catch (err) {
    formError.value = errorMessage(err);
  } finally {
    submitting.value = false;
  }
}

async function toggleStatus(n: NxEndpointView) {
  try {
    await api.nxEndpoints.update(n.id, {
      status: n.status === "enabled" ? "disabled" : "enabled",
    });
    await reload();
  } catch (err) {
    listError.value = errorMessage(err);
  }
}

async function setChannel(n: NxEndpointView, ev: Event) {
  const sel = ev.target as HTMLSelectElement;
  const channel = sel.value as UpdateChannel;
  if (channel === n.update_channel) return;
  try {
    await api.nxEndpoints.update(n.id, { update_channel: channel });
    await reload();
  } catch (err) {
    listError.value = errorMessage(err);
    // §F12: snap the DOM back so it stays in sync with canonical state.
    sel.value = n.update_channel;
  }
}

async function setPolicy(n: NxEndpointView, ev: Event) {
  const sel = ev.target as HTMLSelectElement;
  const policy = sel.value as UpdatePolicy;
  if (policy === n.update_policy) return;
  try {
    await api.nxEndpoints.update(n.id, { update_policy: policy });
    await reload();
  } catch (err) {
    listError.value = errorMessage(err);
    sel.value = n.update_policy;
  }
}

async function remove(n: NxEndpointView) {
  if (!window.confirm(t("admin.confirm.delete_nx", { login: n.login }))) return;
  try {
    await api.nxEndpoints.delete(n.id);
    await reload();
  } catch (err) {
    listError.value = errorMessage(err);
  }
}

function errorMessage(err: unknown): string {
  return describeError(err, t);
}

const filtered = computed(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) return items.value;
  return items.value.filter(
    (n) =>
      n.login.toLowerCase().includes(q) ||
      n.hostname.toLowerCase().includes(q) ||
      (n.display_name?.toLowerCase().includes(q) ?? false),
  );
});

onMounted(reload);
</script>

<template>
  <div class="admin-page">
    <div class="admin-page__header">
      <h1>{{ t("admin.nav.nx_endpoints") }}</h1>
      <input v-model="search" type="search" :placeholder="t('admin.search')" />
    </div>

    <SecretReveal
      v-if="lastSecret"
      :secret="lastSecret.password"
      :label="t('admin.nx.password_shown_once', { login: lastSecret.login })"
      @dismiss="lastSecret = null"
    />

    <section class="admin-card">
      <h2 style="margin-top: 0; font-size: 1rem">{{ t("admin.nx.create_title") }}</h2>
      <form class="admin-form" @submit.prevent="submit">
        <label>
          <span>{{ t("admin.agents.login") }}</span>
          <input v-model="newLogin" required :disabled="submitting" />
        </label>
        <label>
          <span>{{ t("admin.agents.hostname") }}</span>
          <input v-model="newHostname" required :disabled="submitting" />
        </label>
        <label>
          <span>{{ t("admin.agents.display_name") }}</span>
          <input v-model="newDisplay" :disabled="submitting" />
        </label>
        <button type="submit" class="admin-form__submit" :disabled="submitting">
          {{ submitting ? t("admin.submitting") : t("admin.create") }}
        </button>
      </form>
      <p v-if="formError" class="admin-error" style="margin-top: 0.5rem">{{ formError }}</p>
    </section>

    <section class="admin-card">
      <p v-if="loading" class="admin-empty">{{ t("admin.loading") }}</p>
      <p v-else-if="listError" class="admin-error">{{ listError }}</p>
      <p v-else-if="filtered.length === 0" class="admin-empty">{{ t("admin.no_results") }}</p>
      <table v-else class="admin-table">
        <thead>
          <tr>
            <th>{{ t("admin.agents.login") }}</th>
            <th>{{ t("admin.agents.hostname") }}</th>
            <th>{{ t("admin.agents.display_name") }}</th>
            <th>{{ t("admin.status") }}</th>
            <th>{{ t("admin.update.channel") }}</th>
            <th>{{ t("admin.update.policy") }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="n in filtered" :key="n.id">
            <td :data-label="t('admin.agents.login')">{{ n.login }}</td>
            <td :data-label="t('admin.agents.hostname')">{{ n.hostname }}</td>
            <td :data-label="t('admin.agents.display_name')">{{ n.display_name ?? "—" }}</td>
            <td :data-label="t('admin.status')">
              <span class="admin-badge" :data-tone="n.status === 'enabled' ? 'ok' : 'bad'">{{
                t(`admin.status_${n.status}`)
              }}</span>
            </td>
            <td :data-label="t('admin.update.channel')">
              <select :value="n.update_channel" @change="setChannel(n, $event)">
                <option value="canary">canary</option>
                <option value="stable">stable</option>
              </select>
            </td>
            <td :data-label="t('admin.update.policy')">
              <select :value="n.update_policy" @change="setPolicy(n, $event)">
                <option value="manual">manual</option>
                <option value="disabled">disabled</option>
                <option value="auto">auto</option>
                <option value="auto_idle_only">auto_idle_only</option>
              </select>
            </td>
            <td class="admin-table__actions">
              <button type="button" @click="toggleStatus(n)">
                {{ n.status === "enabled" ? t("admin.disable") : t("admin.enable") }}
              </button>
              <button type="button" @click="remove(n)">{{ t("admin.delete") }}</button>
            </td>
          </tr>
        </tbody>
      </table>
    </section>
  </div>
</template>
